// Customer API Service
import { getApiBaseUrl } from '../config';

export interface CustomerRegistrationRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  gender: string;
  birthDate: string | null;
  education: string;
  profession: string;
  country: string;
  region: string;
  city: string;
  district: string;
  address: string;
  isTurkishCitizen: boolean;
  kvkkConsent: boolean;
  etkConsent: boolean;
}

export interface CustomerRegistrationResponse {
  success: boolean;
  message: string;
  customerId: number;
  timestamp: string;
  customer: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    gender: string;
    birthDate: string;
    education: string;
    profession: string;
    country: string;
    region: string;
    city: string;
    district: string;
    address: string;
    isTurkishCitizen: boolean;
    kvkkConsent: boolean;
    etkConsent: boolean;
    createdAt: string;
    isActive: boolean;
    smsCount: number;
    lastSmsDate: string | null;
  };
  smsSent: boolean;
  smsMessage: string;
}

export interface ApiError {
  message: string;
  statusCode: number;
  details?: any;
}

class CustomerApiService {
  private baseUrl: string;

  constructor() {
    // Geliştirmede: sayfa IP ile açıldıysa API aynı host:5001 kullanılır
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * Müşteri kaydı oluşturur (Link ile kayıt için kullanılır)
   * Not: Direk kayıt kaldırıldı, sadece link ile kayıt destekleniyor
   */
  async registerCustomer(customerData: CustomerRegistrationRequest): Promise<CustomerRegistrationResponse> {
    throw new Error('Direk kayıt kaldırıldı. Lütfen kayıt linki kullanın.');
  }

  /**
   * API sağlık kontrolü
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      return response.ok;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }
}

// Singleton instance
export const customerApiService = new CustomerApiService();

// Helper function to validate phone number format
export const validatePhoneNumber = (phoneNumber: string): boolean => {
  // Tüm olmayan karakterleri temizle (sadece rakam bırak)
  let digits = (phoneNumber || '').replace(/\D/g, '');

  // Başta ülke kodu varsa (90) veya +90 ile gelmişse çıkar
  if (digits.startsWith('90')) {
    digits = digits.substring(2);
  }

  // Başta 0 varsa kaldır (0542... -> 542...)
  if (digits.length === 11 && digits.startsWith('0')) {
    digits = digits.substring(1);
  }

  // Türk mobil formatı: 5XXXXXXXXX (10 hane ve 5 ile başlar)
  const turkishMobile = /^5\d{9}$/;
  return turkishMobile.test(digits);
};

// Helper function to format form data for API
export const formatCustomerDataForApi = (formData: any): CustomerRegistrationRequest => {
  // Telefonu normalize et: sadece rakam, baştaki +90/90/0'ı temizle, 10 haneyi al
  let digits = (formData.phoneNumber || '').replace(/\D/g, '');
  if (digits.startsWith('90')) digits = digits.substring(2);
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.substring(1);
  const normalizedPhone = digits;

  return {
    firstName: formData.firstName?.trim() || '',
    lastName: formData.lastName?.trim() || '',
    email: formData.email?.trim() || '',
    phoneNumber: normalizedPhone ? `+90${normalizedPhone}` : '',
    gender: formData.gender || '',
    birthDate: formData.birthDate?.trim() || null, // Boşsa null gönder
    education: formData.education || '',
    profession: formData.profession?.trim() || '',
    country: formData.country || '',
    region: formData.region || '',
    city: formData.city || '',
    district: formData.district || '',
    address: formData.address?.trim() || '',
    isTurkishCitizen: formData.isTurkishCitizen || false,
    kvkkConsent: formData.kvkkConsent || false,
    etkConsent: formData.etkConsent || false,
  };
};
